import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import {
	type EditorState,
	PluginKey,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import type { HubExternalInsertPlugin } from '../hubExternalInsertPluginType';

import {
	createCursorIndicatorDecoration,
	findCursorIndicatorDecs,
} from './create-cursor-indicator-decoration';

const HUB_EXTENSION_DRAG_TYPE = 'hub-extension';

export const key: PluginKey<PluginState> = new PluginKey<PluginState>('hubExternalInsert');

export interface PluginState {
	decorations: DecorationSet;
	/** Document position resolved from the latest mouse coordinates during drag. */
	dropTargetPos: number | null;
	showCursorDropIndicator: boolean;
}

const initialState: PluginState = {
	decorations: DecorationSet.empty,
	showCursorDropIndicator: false,
	dropTargetPos: null,
};

/**
 * Resolves a document position from mouse coordinates by using ProseMirror's
 * posAtCoords API and then walking up to the nearest top-level block boundary.
 * Returns the position at a block boundary (before or after a top-level node),
 * or null if the coordinates don't map to a position inside the editor.
 *
 * This function avoids `coordsAtPos` (which triggers expensive synchronous
 * layout reflow) by using the DOM node returned by `posAtCoords` to determine
 * whether the mouse is in the upper or lower half of the block.
 */
function resolveDropPosition(
	editorView: EditorView,
	clientX: number,
	clientY: number,
): number | null {
	try {
		const posInfo = editorView.posAtCoords({ left: clientX, top: clientY });
		if (!posInfo) {
			return null;
		}
		const resolvedPos = editorView.state.doc.resolve(
			Math.min(posInfo.pos, editorView.state.doc.content.size),
		);

		// Walk up to find the position before the top-level (depth 1) block node.
		// We use depth 1 because depth 0 is the doc node itself.
		if (resolvedPos.depth >= 1) {
			const blockStart = resolvedPos.before(1);
			const blockEnd = resolvedPos.after(1);

			// Determine whether the mouse is in the upper or lower half of the
			// block to decide placement above or below. We read the block's DOM
			// element bounding rect directly instead of calling coordsAtPos
			// (which forces a full synchronous layout reflow and is extremely
			// slow when complex React node views are present).
			const domNode = editorView.nodeDOM(blockStart);
			if (domNode instanceof HTMLElement) {
				const rect = domNode.getBoundingClientRect();
				const blockMidY = (rect.top + rect.bottom) / 2;
				return clientY < blockMidY ? blockStart : blockEnd;
			}

			// Fallback: use the proportional position within the block text
			// content to estimate upper vs lower half.
			const blockSize = blockEnd - blockStart;
			const offsetInBlock = posInfo.pos - blockStart;
			return offsetInBlock < blockSize / 2 ? blockStart : blockEnd;
		}

		return resolvedPos.pos;
	} catch {
		return null;
	}
}

export function createPlugin(
	api: ExtractInjectionAPI<HubExternalInsertPlugin> | undefined,
	nodeViewPortalProviderAPI: PortalProviderAPI | undefined,
): SafePlugin<PluginState> {
	// If portal API is missing (e.g. wrong editor wiring), run as no-op so the page doesn't break
	if (!nodeViewPortalProviderAPI || typeof nodeViewPortalProviderAPI.render !== 'function') {
		return new SafePlugin({
			key,
			state: {
				init: () => initialState,
				apply: (_tr, state) => state,
			},
			props: {
				decorations: (state) => key.getState(state)?.decorations,
			},
		});
	}

	return new SafePlugin({
		key,
		state: {
			init: () => initialState,
			apply: (
				tr: ReadonlyTransaction,
				currentState: PluginState,
				_oldState: EditorState,
				newState: EditorState,
			) => {
				try {
					const meta = tr.getMeta(key) as
						| { dropTargetPos?: number | null; showCursorDropIndicator?: boolean }
						| undefined;

					// If there is no meta for this plugin and the doc hasn't changed,
					// nothing can affect our state — return early to avoid expensive
					// decoration teardown/recreation (which involves React portals).
					if (!meta && !tr.docChanged) {
						return currentState;
					}

					const showCursorDropIndicator =
						meta?.showCursorDropIndicator ?? currentState.showCursorDropIndicator;
					const dropTargetPos =
						meta?.dropTargetPos !== undefined ? meta.dropTargetPos : currentState.dropTargetPos;

					// If nothing meaningful changed, keep the current state as-is
					// to avoid destroying and recreating the React portal decoration.
					if (
						showCursorDropIndicator === currentState.showCursorDropIndicator &&
						dropTargetPos === currentState.dropTargetPos &&
						!tr.docChanged
					) {
						return currentState;
					}

					let decorations = currentState.decorations;

					if (tr.docChanged) {
						decorations = decorations.map(tr.mapping, tr.doc);
					}

					// Only remove and recreate decorations when the indicator
					// visibility or position actually changed.
					const indicatorChanged =
						showCursorDropIndicator !== currentState.showCursorDropIndicator ||
						dropTargetPos !== currentState.dropTargetPos;

					if (indicatorChanged) {
						const cursorDecs = findCursorIndicatorDecs(decorations);
						if (cursorDecs.length > 0) {
							decorations = decorations.remove(cursorDecs);
						}

						if (showCursorDropIndicator && dropTargetPos != null) {
							const clampedPos = Math.min(dropTargetPos, newState.doc.content.size);
							const resolvedPos = newState.doc.resolve(clampedPos);
							const pos = resolvedPos.depth >= 1 ? resolvedPos.before(1) : clampedPos;
							const index = resolvedPos.index(0);
							const prevNode = index > 0 ? newState.doc.child(index - 1) : null;
							const nextNode = index < newState.doc.childCount ? newState.doc.child(index) : null;
							const parentNode = newState.doc;
							const lineLength = api?.width?.sharedState?.currentState()?.lineLength;
							const dec = createCursorIndicatorDecoration(
								pos,
								nodeViewPortalProviderAPI,
								prevNode,
								nextNode,
								parentNode,
								lineLength,
							);
							decorations = decorations.add(newState.doc, [dec]);
						}
					}

					return {
						decorations,
						showCursorDropIndicator,
						dropTargetPos,
					};
				} catch {
					return currentState;
				}
			},
		},
		props: {
			decorations: (state) => key.getState(state)?.decorations,
		},
		view(editorView: EditorView) {
			let cleanup: (() => void) | undefined;
			try {
				// Track pending rAF to throttle onDrag updates to at most once per frame.
				let rafId: number | null = null;

				cleanup = monitorForElements({
					canMonitor: ({ source }) => source.data.type === HUB_EXTENSION_DRAG_TYPE,
					onDragStart: ({ location }) => {
						const coords = location.current.input;
						const dropPos = resolveDropPosition(editorView, coords.clientX, coords.clientY);
						editorView.dispatch(
							editorView.state.tr.setMeta(key, {
								showCursorDropIndicator: true,
								dropTargetPos: dropPos,
							}),
						);
					},
					onDrag: ({ location }) => {
						const coords = location.current.input;
						const clientX = coords.clientX;
						const clientY = coords.clientY;

						// Throttle to one update per animation frame to avoid excessive
						// ProseMirror transactions while the user is dragging.
						if (rafId != null) {
							cancelAnimationFrame(rafId);
						}
						rafId = requestAnimationFrame(() => {
							rafId = null;
							const dropPos = resolveDropPosition(editorView, clientX, clientY);
							const currentState = key.getState(editorView.state);
							// Only dispatch if position actually changed to avoid unnecessary updates
							if (currentState?.dropTargetPos !== dropPos) {
								editorView.dispatch(editorView.state.tr.setMeta(key, { dropTargetPos: dropPos }));
							}
						});
					},
					onDrop: () => {
						if (rafId != null) {
							cancelAnimationFrame(rafId);
							rafId = null;
						}
						// Always ensure the indicator is cleaned up when a drag
						// ends. The EditorDropTarget's onDrop already clears the
						// indicator and inserts content in its own transaction,
						// but we still need this for drags that end outside the
						// drop target (cancelled or missed). The apply function's
						// early-return optimisation ensures this is a no-op when
						// the indicator was already cleared by EditorDropTarget.
						const currentState = key.getState(editorView.state);
						if (currentState?.showCursorDropIndicator) {
							editorView.dispatch(
								editorView.state.tr.setMeta(key, {
									showCursorDropIndicator: false,
									dropTargetPos: null,
								}),
							);
						}
					},
				});
			} catch {}
			return {
				destroy() {
					try {
						cleanup?.();
					} catch {}
				},
			};
		},
	});
}
